import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { SITE } from "../data/site";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-red">
            · Visit Us
          </span>
          <h2 className="mt-3 font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-brand-charcoal">
            Find your seat<br />
            at our <span className="text-brand-red italic">table.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 bg-brand-charcoal text-brand-cream rounded-3xl p-8 md:p-10 grain-overlay relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-red/20 blur-3xl" />

            <div className="relative space-y-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand-red/20 text-brand-mustard flex items-center justify-center">
                    <MapPin size={18} />
                  </div>
                  <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-mustard">
                    Where
                  </span>
                </div>
                <p className="font-heading text-xl md:text-2xl leading-snug font-semibold">
                  {SITE.address}
                </p>
                <a
                  href={SITE.directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-link"
                  className="mt-3 inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.2em] font-bold text-brand-mustard hover:gap-3 transition-all"
                >
                  Get Directions <Navigation size={12} />
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand-red/20 text-brand-mustard flex items-center justify-center">
                    <Clock size={18} />
                  </div>
                  <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-mustard">
                    When
                  </span>
                </div>
                <div className="space-y-2.5">
                  {SITE.hours.map((h) => (
                    <div
                      key={h.day}
                      className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1 pb-2 border-b border-brand-cream/10 last:border-0"
                    >
                      <span className="font-heading uppercase text-[11px] tracking-[0.18em] font-bold text-brand-cream/90">
                        {h.day}
                      </span>
                      <span className="text-sm text-brand-cream/70 font-body">
                        {h.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand-red/20 text-brand-mustard flex items-center justify-center">
                    <Phone size={18} />
                  </div>
                  <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-mustard">
                    Talk
                  </span>
                </div>
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-phone"
                  className="font-display text-3xl md:text-4xl text-brand-cream hover:text-brand-mustard transition-colors"
                >
                  {SITE.phone}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 rounded-3xl overflow-hidden border border-brand-border bg-white min-h-[460px]"
          >
            <iframe
              src={SITE.mapUrl}
              title="Red Chilly's location map"
              data-testid="location-map"
              className="w-full h-full min-h-[460px] grayscale-[20%] contrast-105"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
